import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  DateRangePicker,
  EmptyState,
  ErrorState,
  FilterChip,
  Search,
  Select,
  Skeleton,
  Table,
  type TableColumn,
} from '@indipia/ui-core';
import { useInfiniteList, type InfiniteQuery } from './useInfiniteList';
import { ChannelBadge, ReadBadge, ReadCountBadge } from './badges';
import type {
  ShareHistoryRecipient,
  ShareHistoryRow,
  ShareHistoryScreenProps,
  ShareTypeOption,
} from './types';

// 공유이력 3메뉴 공용 화면 — 테이블 + 무한스크롤 + 행 인라인 펼침.
// 표현·필터·무한스크롤·펼침은 공용, 데이터(fetchPage)·상세(renderDetail)·조치(actions)는 주입.
// 근거: 공유이력_일관화_개발설계서 §4.

// ── 날짜 유틸 ──
const pad2 = (n: number) => String(n).padStart(2, '0');
const toYmd = (d: Date) => `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
const toDash = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())}`;
};

// ── 기간 프리셋(공사현황 방식 4종) ──
type DatePreset = '7d' | 'lw' | 'tw' | 'tm';
const DATE_PRESETS: readonly (readonly [DatePreset, string])[] = [
  ['7d', '최근 7일'],
  ['lw', '지난주'],
  ['tw', '이번주'],
  ['tm', '이번달'],
] as const;

function presetRange(preset: DatePreset): { from: Date; to: Date } {
  const to = new Date();
  if (preset === '7d') {
    const from = new Date(to);
    from.setDate(to.getDate() - 6);
    return { from, to };
  }
  const dow = (to.getDay() + 6) % 7; // 월=0
  if (preset === 'tw') {
    const from = new Date(to);
    from.setDate(to.getDate() - dow);
    return { from, to };
  }
  if (preset === 'tm') {
    const from = new Date(to.getFullYear(), to.getMonth(), 1);
    return { from, to };
  }
  const thisMonday = new Date(to);
  thisMonday.setDate(to.getDate() - dow);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);
  const lastSunday = new Date(lastMonday);
  lastSunday.setDate(lastMonday.getDate() + 6);
  return { from: lastMonday, to: lastSunday };
}

/** 기본 조회기간 — 최근 N개월(확정값 3) */
function monthsAgoRange(months: number): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to);
  from.setMonth(to.getMonth() - months);
  return { from, to };
}

interface Committed {
  dateFrom: string;
  dateTo: string;
  siteId?: number;
  typeCode?: string;
  viewStatus?: 'VIEWED' | 'NOT_VIEWED';
  keyword?: string;
}

export function ShareHistoryScreen({
  title,
  description,
  typeOptions,
  typeAllLabel = '전체 유형',
  siteOptions,
  keywordPlaceholder = '수신인 검색',
  fetchPage,
  renderDetail,
  actions,
  holidays,
  defaultMonths = 3,
  pageSize = 20,
}: ShareHistoryScreenProps) {
  // ── 조회조건(폼 초안) — [조회] 버튼으로 committed 반영 ──
  const [preset, setPreset] = useState<DatePreset | null>(null); // 기본은 3개월(custom), 프리셋 미선택
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(() => {
    const r = monthsAgoRange(defaultMonths);
    return { from: toDash(r.from), to: toDash(r.to) };
  });
  const [siteIdInput, setSiteIdInput] = useState<number | ''>('');
  const [typeInput, setTypeInput] = useState('');
  const [viewStatusInput, setViewStatusInput] = useState<'' | 'VIEWED' | 'NOT_VIEWED'>('');
  const [keywordInput, setKeywordInput] = useState('');
  const [dirty, setDirty] = useState(false);
  const [sortDesc, setSortDesc] = useState(true);

  const initial = monthsAgoRange(defaultMonths);
  const [committed, setCommitted] = useState<Committed>({
    dateFrom: toYmd(initial.from),
    dateTo: toYmd(initial.to),
  });

  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());
  const [busyId, setBusyId] = useState<string | number | null>(null);

  const markDirty = () => setDirty(true);

  // 화면에 표시되는 "현재 선택된 기간" — 직접설정 우선, 없으면 프리셋, 둘 다 없으면 기본 3개월
  const displayRange = customRange
    ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
    : preset
      ? presetRange(preset)
      : monthsAgoRange(defaultMonths);

  const applyQuery = () => {
    setCommitted({
      dateFrom: toYmd(displayRange.from),
      dateTo: toYmd(displayRange.to),
      siteId: siteIdInput === '' ? undefined : siteIdInput,
      typeCode: typeInput || undefined,
      viewStatus: viewStatusInput || undefined,
      keyword: keywordInput.trim() || undefined,
    });
    setDirty(false);
  };

  const query: InfiniteQuery = useMemo(
    () => ({
      dateFrom: committed.dateFrom,
      dateTo: committed.dateTo,
      siteId: committed.siteId,
      typeCode: committed.typeCode,
      viewStatus: committed.viewStatus,
      keyword: committed.keyword,
      sortDesc,
    }),
    [committed, sortDesc],
  );

  const { items, state, hasNext, loadingMore, scrollRef, sentinelRef, reload } = useInfiniteList(
    fetchPage,
    query,
    pageSize,
  );

  const toggleExpand = (id: string | number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runAction = (
    id: string | number,
    fn: () => Promise<void> | void,
    okMsg: string,
    failMsg: string,
  ) => {
    setBusyId(id);
    Promise.resolve(fn())
      .then(() => {
        toast.success(okMsg);
        reload();
      })
      .catch(() => toast.error(failMsg))
      .finally(() => setBusyId(null));
  };

  const typeMap = useMemo(() => {
    const m = new Map<string, ShareTypeOption>();
    (typeOptions ?? []).forEach((t) => m.set(t.value, t));
    return m;
  }, [typeOptions]);

  // 현장 필터 옵션(표준 Combobox) — '전체 현장' + 현장 목록. value 는 siteId 문자열
  const siteComboOptions = useMemo<ComboboxOption[]>(() => {
    if (!siteOptions) return [];
    return [
      { value: '', label: '전체 현장' },
      ...siteOptions.map((s) => ({ value: String(s.siteId), label: s.siteName ?? `현장 #${s.siteId}` })),
    ];
  }, [siteOptions]);

  const columns = useMemo<TableColumn<ShareHistoryRow>[]>(() => {
    const cols: TableColumn<ShareHistoryRow>[] = [];
    if (typeOptions && typeOptions.length > 0) {
      cols.push({
        key: 'type',
        header: '유형',
        align: 'left',
        width: 'var(--spacing-12)',
        render: (row) => {
          const t = row.typeCode ? typeMap.get(row.typeCode) : undefined;
          if (!t) return <span className="text-text-muted">-</span>;
          return (
            <Badge status={t.badgeStatus} className="whitespace-nowrap">
              {t.label}
            </Badge>
          );
        },
      });
    }
    cols.push(
      {
        key: 'site',
        header: '현장·대상',
        align: 'left',
        render: (row) => <span className="font-bold">{row.siteName ?? '-'}</span>,
      },
      {
        key: 'recipient',
        header: '수신인',
        align: 'left',
        render: (row) => <RecipientSummary recipients={row.recipients} />,
      },
      {
        key: 'sentAt',
        header: '발송시각',
        align: 'center',
        width: 'var(--spacing-13)',
        render: (row) => <span className="whitespace-nowrap text-text-muted">{fmtDateTime(row.sentAt)}</span>,
      },
      {
        key: 'expiresAt',
        header: '유효기간',
        align: 'center',
        width: 'var(--spacing-12)',
        render: (row) => <span className="whitespace-nowrap text-text-muted">{fmtDate(row.expiresAt)}</span>,
      },
      {
        key: 'read',
        header: '열람',
        align: 'center',
        width: 'var(--spacing-12)',
        render: (row) => <ReadCell recipients={row.recipients} />,
      },
      {
        key: 'channel',
        header: '매체',
        align: 'center',
        width: 'var(--spacing-12)',
        render: (row) => <ChannelCell recipients={row.recipients} />,
      },
      {
        key: 'action',
        header: '',
        align: 'center',
        width: 'var(--spacing-13)',
        render: (row) => (
          <div className="flex items-center justify-center gap-02" onClick={(e) => e.stopPropagation()}>
            <PreviewButton recipients={row.recipients} />
            {actions?.onResend && row.resendable && (
              <Button
                variant="secondary"
                size="sm"
                loading={busyId === row.id}
                onClick={() =>
                  runAction(
                    row.id,
                    () => actions.onResend!(row),
                    '재발송 완료',
                    '재발송에 실패했습니다.',
                  )
                }
              >
                재발송
              </Button>
            )}
            {actions?.onRevoke && row.revokable && (
              <Button
                variant="etc"
                size="sm"
                loading={busyId === row.id}
                onClick={() =>
                  runAction(
                    row.id,
                    () => actions.onRevoke!(row),
                    '공유를 취소했습니다.',
                    '공유 취소에 실패했습니다.',
                  )
                }
              >
                공유취소
              </Button>
            )}
          </div>
        ),
      },
    );
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOptions, typeMap, actions, busyId]);

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="px-07 pt-06 pb-04">
        <h1 className="text-heading-md font-bold text-text">{title}</h1>
        {description && <p className="mt-02 text-body-sm text-text-muted">{description}</p>}
      </div>

      {/* ── 조회조건 카드 — [조회] 버튼 트리거 ── */}
      <div className="mx-07 flex flex-col gap-sm rounded-xl border border-border bg-surface-raised p-05">
        <div className="flex flex-wrap items-center gap-sm">
          {siteOptions && (
            <Combobox
              aria-label="현장"
              searchable
              value={siteIdInput === '' ? '' : String(siteIdInput)}
              options={siteComboOptions}
              placeholder="전체 현장"
              style={{ width: 'var(--spacing-13)' }}
              onChange={(v) => {
                setSiteIdInput(v === '' ? '' : Number(v));
                markDirty();
              }}
            />
          )}

          {typeOptions && typeOptions.length > 0 && (
            <Select
              aria-label="유형"
              value={typeInput}
              onChange={(e) => {
                setTypeInput(e.target.value);
                markDirty();
              }}
            >
              <option value="">{typeAllLabel}</option>
              {typeOptions.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          )}

          <Select
            aria-label="열람상태"
            value={viewStatusInput}
            onChange={(e) => {
              setViewStatusInput(e.target.value as typeof viewStatusInput);
              markDirty();
            }}
          >
            <option value="">전체 열람상태</option>
            <option value="VIEWED">열람</option>
            <option value="NOT_VIEWED">미열람</option>
          </Select>

          <Search
            value={keywordInput}
            onChange={(v) => {
              setKeywordInput(v);
              markDirty();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyQuery();
            }}
            placeholder={keywordPlaceholder}
            width="auto"
          />

          <Button
            size="md"
            onClick={applyQuery}
            style={dirty ? { boxShadow: '0 0 0 3px var(--status-info-200)' } : undefined}
          >
            조회
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-sm border-t border-dashed border-border pt-03">
          <span className="text-body-sm font-bold text-text-secondary">조회기간</span>
          <DateRangePicker
            value={{ start: displayRange.from, end: displayRange.to }}
            onChange={({ start, end }) => {
              if (start && end) {
                setCustomRange({ from: toDash(start), to: toDash(end) });
                setPreset(null);
                markDirty();
              }
            }}
            active={!!customRange}
            holidays={holidays}
            presets={false}
            size="sm"
            aria-label="조회기간 직접 설정"
          />

          {DATE_PRESETS.map(([key, label]) => (
            <FilterChip
              key={key}
              active={!customRange && preset === key}
              onClick={() => {
                setCustomRange(null);
                setPreset(key);
                markDirty();
              }}
            >
              {label}
            </FilterChip>
          ))}

          <Button variant="etc" size="md" className="ml-auto" onClick={() => setSortDesc((v) => !v)} aria-label="정렬">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
              <path d="M12 5v14" />
              {sortDesc ? <path d="M5 12l7-7 7 7" /> : <path d="M19 12l-7 7-7-7" />}
            </svg>
            {sortDesc ? '최신순' : '과거순'}
          </Button>
        </div>
      </div>

      {/* ── 목록 ── */}
      <div ref={scrollRef} className="flex-1 overflow-auto px-07 pt-03 pb-08">
        {state === 'ready' && items.length > 0 && (
          <div className="mb-03 flex items-center gap-sm">
            <h2 className="text-body-md font-bold text-text">공유 이력</h2>
            <Badge status="info">{items.length}건</Badge>
          </div>
        )}

        {state === 'loading' && (
          <div className="space-y-04">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={48} className="rounded-md" />
            ))}
          </div>
        )}

        {state === 'error' && <ErrorState onRetry={reload} />}

        {state === 'ready' && items.length === 0 && <EmptyState title="조회된 발송 이력이 없습니다" />}

        {state === 'ready' && items.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface-raised">
            <Table
              columns={columns}
              rows={items}
              rowKey={(row) => row.id}
              onRowClick={(row) => toggleExpand(row.id)}
              expandedKeys={expandedIds}
              renderExpanded={(row) => renderDetail(row, { reload })}
              stickyHeader
              style={{ minWidth: 1000 }}
            />
            {hasNext && (
              <div ref={sentinelRef} className="py-05 text-center text-body-sm text-text-muted">
                {loadingMore ? '불러오는 중입니다.' : ''}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 셀 보조 컴포넌트 ──

function RecipientSummary({ recipients }: { recipients: ShareHistoryRecipient[] }) {
  if (recipients.length === 0) return <span className="text-text-muted">-</span>;
  const head = recipients[0];
  return (
    <span className="whitespace-nowrap">
      {head.name}
      {head.role ? <span className="ml-02 text-caption text-text-muted">{head.role}</span> : null}
      {recipients.length > 1 && (
        <Badge status="mute" className="ml-02">
          외 {recipients.length - 1}명
        </Badge>
      )}
    </span>
  );
}

function ReadCell({ recipients }: { recipients: ShareHistoryRecipient[] }) {
  if (recipients.length === 0) return <span className="text-text-muted">-</span>;
  if (recipients.length === 1) return <ReadBadge viewed={recipients[0].viewed} />;
  const viewed = recipients.filter((r) => r.viewed).length;
  return <ReadCountBadge viewed={viewed} total={recipients.length} />;
}

function ChannelCell({ recipients }: { recipients: ShareHistoryRecipient[] }) {
  const channels = Array.from(new Set(recipients.map((r) => r.channel).filter(Boolean))) as string[];
  if (channels.length === 0) return <span className="text-text-muted">-</span>;
  if (channels.length === 1) return <ChannelBadge channel={channels[0]} />;
  return (
    <div className="flex flex-wrap items-center justify-center gap-01">
      {channels.map((c) => (
        <ChannelBadge key={c} channel={c} />
      ))}
    </div>
  );
}

/** 미리보기(새 창) — 단건 수신자이고 previewUrl 이 있을 때만 행에 노출 */
function PreviewButton({ recipients }: { recipients: ShareHistoryRecipient[] }) {
  if (recipients.length !== 1) return null;
  const url = recipients[0].previewUrl;
  if (!url) return null;
  return (
    <Button variant="etc" size="sm" onClick={() => window.open(url, '_blank', 'noopener')}>
      미리보기
    </Button>
  );
}
